// src/components/white-screen-tool/ScreenCard.tsx
import { Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ScreenCardProps {
  color: string;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

const ScreenCard = ({ color, isFullscreen, onToggleFullscreen }: ScreenCardProps) => {
  // Replace deprecated navigator.platform with modern userAgent check
  const isMac = /Mac|iPhone|iPod|iPad/.test(navigator.userAgent);
  const exitText = isMac ? "Press ⌘+⌃+F to exit" : "Press Esc to exit";
  const isWhite = color.toLowerCase() === '#ffffff';
  const isBlack = color.toLowerCase() === '#000000';

  return (
    <div className="w-full">
      <div className="relative aspect-video rounded-xl overflow-hidden border border-gray-200/50 bg-white shadow-lg">
        <div
          className="w-full h-full transition-colors duration-200"
          style={{ backgroundColor: color }}
          role="img"
          aria-label={`Color preview: ${color}`}
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={`absolute bottom-4 right-4 backdrop-blur-sm border ${
                  isWhite 
                    ? 'bg-gray-100 hover:bg-gray-200 border-gray-300' 
                    : isBlack
                      ? 'bg-white hover:bg-gray-100 border-white text-black'
                      : 'bg-white/20 hover:bg-white/30 border-white/30'
                }`}
                onClick={onToggleFullscreen}
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize2 className={`h-4 w-4 ${isWhite ? 'text-gray-700' : isBlack ? 'text-black' : ''}`} />
                ) : (
                  <Maximize2 className={`h-4 w-4 ${isWhite ? 'text-gray-700' : isBlack ? 'text-black' : ''}`} />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isFullscreen ? exitText : "Enter fullscreen"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default ScreenCard;